import React, { useEffect, useRef, useState } from "react";
import {
  Landmark, ShieldCheck, Stethoscope, ShoppingBag,
  Signal, Scale, Truck, Building2,
} from "lucide-react";
import LiveCallsPanel from "./LiveCallsPanel";
import { useT } from "../context/LanguageContext";

const iconMap = {
  Landmark,
  ShieldCheck,
  Stethoscope,
  ShoppingBag,
  Signal,
  Scale,
  Truck,
  Building2,
};

const IndustriesSection = () => {
  const t = useT();
  const industries = [
    { name: t("industries.card.bank.name"), desc: t("industries.card.bank.description"), icon: "Landmark" },
    { name: t("industries.card.insurance.name"), desc: t("industries.card.insurance.description"), icon: "ShieldCheck" },
    { name: t("industries.card.health.name"), desc: t("industries.card.health.description"), icon: "Stethoscope" },
    { name: t("industries.card.ecommerce.name"), desc: t("industries.card.ecommerce.description"), icon: "ShoppingBag" },
    { name: t("industries.card.telecom.name"), desc: t("industries.card.telecom.description"), icon: "Signal" },
    { name: t("industries.card.legal.name"), desc: t("industries.card.legal.description"), icon: "Scale" },
    { name: t("industries.card.logistics.name"), desc: t("industries.card.logistics.description"), icon: "Truck" },
    { name: t("industries.card.public.name"), desc: t("industries.card.public.description"), icon: "Building2" },
  ];
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => e.isIntersecting && (setVisible(true), obs.disconnect()),
      { threshold: 0.1 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <section
      id="industries"
      ref={ref}
      className={`section-fade ${visible ? "visible" : ""} py-20 px-6 flex items-center`}
      style={{ minHeight: "100vh", background: "var(--section-surface-3)" }}
    >
      <div className="max-w-6xl mx-auto w-full">
        <div className="space-y-10">
          <div className="grid lg:grid-cols-[1.2fr_0.8fr] gap-10 items-start">
            <div>
              <span
                className="inline-block px-3 py-1 rounded-full text-[11px] font-semibold uppercase tracking-wider mb-4"
                style={{
                  background: "linear-gradient(135deg, rgba(75,111,212,0.12), rgba(20,184,166,0.10))",
                  border: "1px solid rgba(75,111,212,0.2)",
                  color: "var(--brand-cyan)",
                }}
              >
                {t("industries.eyebrow")}
              </span>
              <h2 className="font-bold tracking-tight" style={{ color: "var(--fg)", fontSize: "clamp(2rem, 4vw, 3rem)" }}>
                {t("industries.title1")} {" "}
                <span className="brand-gradient-text">{t("industries.title2")}</span>
                {t("industries.title3")}
              </h2>
              <p className="mt-4 text-[15px]" style={{ color: "var(--fg)", opacity: 0.92 }}>
                {t("industries.subtitle")}
              </p>
            </div>

            <div className="lg:pl-2">
              <LiveCallsPanel />
            </div>
          </div>

          {/* Industries visual */}
          <div className="mb-8 rounded-2xl overflow-hidden" style={{ border: "1px solid var(--border)" }}>
            <img
              src="/images/pages/hero-industries.jpg"
              alt="Farklı sektörler ve profesyoneller"
              className="w-full h-auto object-cover"
              style={{ maxHeight: "280px" }}
              loading="lazy"
            />
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {industries.map((ind, index) => {
              const Icon = iconMap[ind.icon];
              const iconColors = ["#4B6FD4", "#FF7A7A", "#14B8A6", "#8B5CF6"];
              const iconColor = iconColors[index % iconColors.length];
              return (
                <div
                  key={ind.name}
                  className="p-5 rounded-2xl transition-colors duration-200 neon-box"
                  style={{
                    background: "var(--card-bg)",
                    border: "1px solid var(--border)",
                  }}
                >
                  <div
                    className="w-10 h-10 rounded-xl flex items-center justify-center mb-3"
                    style={{
                      background: `${iconColor}14`,
                      color: iconColor,
                    }}
                  >
                    {Icon ? <Icon size={18} /> : null}
                  </div>
                  <h3
                    className="text-[14.5px] font-semibold mb-1"
                    style={{ color: "var(--fg)" }}
                  >
                    {ind.name}
                  </h3>
                  <p
                    className="text-[12.5px] leading-snug"
                    style={{ color: "var(--fg)", opacity: 0.9 }}
                  >
                    {ind.desc}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default IndustriesSection;
